export type BpVerdict = "crisis" | "high" | "low";

/**
 * Classify a blood-pressure reading from BOTH numbers together.
 *
 * Returns a single verdict (most-severe-first) or `null` for a normal or
 * incomplete reading. This replaces the previous two independent per-field
 * checks (systolic and diastolic warned separately), so the reading is judged
 * as a whole — e.g. an isolated diastolic of 115 is a hypertensive crisis even
 * when the systolic looks normal, which the per-field logic could miss.
 *
 * Thresholds mirror the agency critical-alert rules (crisis 180/110,
 * hypotension 90/55) plus a stage-2 "high" tier (140/90).
 */
export function classifyBp(sysStr: string, diaStr: string): BpVerdict | null {
    if (!sysStr || !diaStr) {
        return null;
    }
    const sys = parseFloat(sysStr);
    const dia = parseFloat(diaStr);
    if (isNaN(sys) || isNaN(dia)) {
        return null;
    }
    if (sys >= 180 || dia >= 110) {
        return "crisis";
    }
    if (sys < 90 || dia < 55) {
        return "low";
    }
    if (sys >= 140 || dia >= 90) {
        return "high";
    }
    return null;
}

export const BP_VERDICT_MESSAGE: Record<BpVerdict, string> = {
    crisis: "hypertensive crisis — verify and act per protocol.",
    high: "elevated (Stage 2).",
    low: "hypotension — verify."
};

/**
 * The plain status word stored in the companion "BP status" question, so the
 * branching engine can gate follow-ups on a value it can read (BP's own answer
 * is `{"systolic","diastolic"}` JSON, which the numeric operators can't parse).
 * Includes "normal"; returns "" for an incomplete or non-numeric reading.
 */
export function bpStatusValue(
    sysStr: string,
    diaStr: string
): "crisis" | "high" | "low" | "normal" | "" {
    if (!sysStr || !diaStr) {
        return "";
    }
    if (isNaN(parseFloat(sysStr)) || isNaN(parseFloat(diaStr))) {
        return "";
    }
    return classifyBp(sysStr, diaStr) ?? "normal";
}

/**
 * Resolve the companion "BP status" question id from the BP question's form
 * config (`{ "statusQuestionId": <id> }`). Returns null when the form doesn't
 * wire one — in which case the field simply doesn't mirror its verdict
 * anywhere (no-op), so the code is safe in environments without the companion.
 */
export function statusQuestionIdFromConfig(
    config: Record<string, unknown> | null | undefined
): string | null {
    const raw = config?.statusQuestionId;
    if (typeof raw === "string" && raw.length > 0) {
        return raw;
    }
    if (typeof raw === "number") {
        return String(raw);
    }
    return null;
}
